import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {Dropdown, DropdownModule} from 'primeng/dropdown';
import {BehaviorSubject, Observable, filter, of, retry, shareReplay, take, tap} from 'rxjs';
import {CreateEditCompanyComponent} from 'src/app/company/create-edit-company/create-edit-company.component';
import {CompanyDetails} from 'src/app/dashboard/models/companyDetails.model';
import {DialogService} from 'src/app/services/dialog.service';
import {CompanyMsService} from 'src/app/services/shared/company-ms.service';
import {TerraUtils} from 'src/app/shared/TerraUtils';
import {RadioBtn, RadioButtonsComponent} from 'src/app/shared/components/form/radio-buttons/radio-buttons.component';
import {BusinessEntityType} from 'src/app/shared/enums/BusinessEntityType.enum';
import {DialogWithFormBase} from 'src/app/shared/types/DialogWithFormBase';
import AccountType from 'src/app/shared/enums/AccountType.enum';
import {BankAccountsMsService} from '../../bank-accounts-ms.service';
import {ExternalClientBankAccountDto} from 'src/app/dashboard/bankAccounts/models/externalClientBankAccountDto.model';
import {UtilsService} from 'src/app/services/utils.service';
import {BaseResponseDto} from 'src/app/shared/models/BaseResponseDto.model';
import {HoldingAssign} from 'src/app/dashboard/models/holdingAssign.model';
import {EntityType} from '../entity-type.enum';
import {CommonModule} from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatInputModule} from '@angular/material/input';
import {ButtonsComponent} from 'src/app/shared/components/buttons/buttons.component';
import {DialogHeaderComponent} from 'src/app/shared/components/dialog-header/dialog-header.component';
import {EnumStringPipe} from 'src/app/shared/pipes/EnumToString.pipe';
import {LoaderComponent} from 'src/app/shared/loader/loader.component';
import {AccountNumberToAstrixPipe} from 'src/app/shared/pipes/account-number-to-astrix.pipe';
import {TooltipModule} from 'primeng/tooltip';

export class EditAccountDetailsDialogContext {
  public clientBankAccountId: number;
  public bankName: string;
  public nickname: string;
  public accountType: AccountType;
  public holdingId: number;
  public companyDetailsId: string;
  public accountNumber: string;
  entityType: string;
}

const ANGULAR_MATERIAL = [MatIconModule, MatButtonModule, MatDialogModule, MatInputModule];
const PRIME_NG = [DropdownModule, TooltipModule];
const STANDALONE_COMPONENT = [DialogHeaderComponent, ButtonsComponent, RadioButtonsComponent, LoaderComponent];
const STANDALONE_PIPE = [EnumStringPipe, AccountNumberToAstrixPipe];

@Component({
  selector: 'terra-edit-account-details-dialog',
  templateUrl: './edit-account-details-dialog.component.html',
  styleUrls: ['./edit-account-details-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ...ANGULAR_MATERIAL,
    ...PRIME_NG,
    ...STANDALONE_COMPONENT,
    ...STANDALONE_PIPE],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditAccountDetailsDialogComponent extends DialogWithFormBase implements OnInit {
  pageForm: UntypedFormGroup;
  EntityType = EntityType;
  AccountType = AccountType;
  accountTypeOption = AccountType.listAll();

  entityTypes = [
    {
      label: "Holding",
      value: EntityType.Holding,
      haveAccountType: true,
      isDefault: true,
      selectingEntityPlaceholderName: "holding",
      selectingEntityPlaceholderlabel: "Select holding",
      selectingEntityFormControlName: "holding",
      emptyStateText: "No holdings added yet.",
      selectingEntitySource: this.bankAccountsMsService.getHoldingForAssignAccount().pipe(shareReplay(1)),
      shouldHaveAddFooter: false,
      itemLabelBuilder: (entity: HoldingAssign) => entity.name
    },
    {
      label: "GP Level",
      value: EntityType.GpLevel,
      haveAccountType: false,
      isDefault: false,
      selectingEntityPlaceholderName: "business entity",
      selectingEntityPlaceholderlabel: "Business entity",
      selectingEntityFormControlName: "companyDetailsId",
      emptyStateText: "No business entities added yet.",
      selectingEntitySource: this.companyMsService.companies$.pipe(shareReplay(1)),
      shouldHaveAddFooter: true,
      itemLabelBuilder: (entity: CompanyDetails) => `${entity.name} ${BusinessEntityType.toString(entity.entityType)}`
    }
  ];

  public get selectedEntityType(): {
    label: string;
    value: EntityType;
    haveAccountType: boolean;
    isDefault: boolean;
    selectingEntityPlaceholderName: string;
    selectingEntityPlaceholderlabel: string;
    selectingEntityFormControlName: string;
    emptyStateText: string;
    selectingEntitySource: Observable<any>;
    shouldHaveAddFooter: boolean;
    itemLabelBuilder: (entity: any) => string;
  } {
    return this.entityTypes.find(type => type.value == this.pageForm.get('entityType')?.value)
  }

  entityTypesButtons: RadioBtn[] = this.entityTypes.map(type => ({
    value: type.value,
    text: type.label,
    checked: false,
  } as RadioBtn))

  isSubmitted = false;
  isGeneralServerError$ = new BehaviorSubject<boolean>(false);
  generalServerErrorMessage = TerraUtils.consts.messages.GENERAL_SUBMIT_ERROR_WITH_LINK;

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<EditAccountDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditAccountDetailsDialogContext,
    private dialogService: DialogService,
    private companyMsService: CompanyMsService,
    private bankAccountsMsService: BankAccountsMsService,
    private utilsService: UtilsService,
    private cdf: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.generateForm();
    this.pageForm.get('entityType').valueChanges.pipe(tap(_ => this.changeValidatorsAccordingToSelectedEntityType())).subscribe();
  }

  generateForm() {
    this.pageForm = this.fb.group({
      nickname: [this.data.nickname, Validators.required],
      entityType: [this.entityTypeConvertor(this.data.entityType), Validators.required],
      holding: [this.data.holdingId, this.entityTypeConvertor(this.data.entityType) === EntityType.Holding ? [Validators.required] : []],
      accountType: this.data.accountType,
      companyDetailsId: [this.data.companyDetailsId, this.entityTypeConvertor(this.data.entityType) === EntityType.GpLevel ? [Validators.required] : []]
    });
    this.pageForm.get('accountType').setValidators(this.selectedEntityType.haveAccountType ? [Validators.required] : []);
  }

  changeValidatorsAccordingToSelectedEntityType(): void {
    const selectedEntityType = this.selectedEntityType;
    this.pageForm.get(selectedEntityType.selectingEntityFormControlName).setValidators(Validators.required);
    selectedEntityType.haveAccountType ? this.pageForm.get('accountType').setValidators(Validators.required) : this.pageForm.get('accountType').clearValidators();
    this.pageForm.get(selectedEntityType.selectingEntityFormControlName).updateValueAndValidity();
    const nonSelected = this.entityTypes.filter(entityType => entityType.value != selectedEntityType.value);
    nonSelected.forEach(nonSelectedEntityType => {
      this.pageForm.get(nonSelectedEntityType.selectingEntityFormControlName).clearValidators();
      this.pageForm.get(nonSelectedEntityType.selectingEntityFormControlName).updateValueAndValidity();
    });
  }

  save() {
    this.pageForm.markAllAsTouched();
    this.pageForm.updateValueAndValidity();

    if (this.pageForm.invalid) {
      return;
    }

    const formData = this.pageForm.value;

    const model = new ExternalClientBankAccountDto();

    model.id = this.data.clientBankAccountId;
    model.nickname = formData.nickname;
    model.accountType = formData.accountType;
    model.holdingId = formData.holding;
    model.companyDetailsId = formData.companyDetailsId;
    this.bankAccountsMsService.update(model.id, model).pipe(
      take(1),

    ).subscribe({
      next: res => {
        this.dialogRef.close(true);
      },
      error: err => {
        if (err instanceof BaseResponseDto) {
          this.utilsService.alertErrorMessage(err);
        } else {
          console.error(err);
          // this.snackbarService.showGeneralMessage(`Failed to ${this.context.createMode ? 'create new user' : 'update user permission'}`);
        }
      }
    })

  }

  getSelectEntityPlaceholder() {
    return `Select ${this.selectedEntityType?.selectingEntityPlaceholderName} associated with the account`
  }

  @ViewChild('businessTypeSelect') businessTypeSelect: Dropdown
  createEditCompany(companyDetails: CompanyDetails) {
    this.dialogService.customDialog<CreateEditCompanyComponent>(CreateEditCompanyComponent, companyDetails, true, 'create-edit-company').afterClosed().pipe(
      take(1),
      filter(company => !!company)
    ).subscribe(company => {
      this.pageForm.get('companyDetailsId').setValue(company.id);
      this.pageForm.get('companyDetailsId').updateValueAndValidity();
      this.businessTypeSelect.writeValue(company.id);
      this.cdf.detectChanges();
    })
  }

  private entityTypeConvertor(entityTypeStr: string): EntityType {
    if (entityTypeStr?.toLowerCase() === 'business') {
      return EntityType.GpLevel;
    }
    return EntityType.Holding;
  }
}
